export type ThinkingEffort = "off" | "low" | "medium" | "high" | "xhigh" | "max";

export type Props = {
  value: ThinkingEffort;
  onChange: (v: ThinkingEffort) => void;
  /** All effort levels the system understands, returned by GET /api/models. */
  all: ThinkingEffort[];
  /** Subset valid for the currently-selected model. */
  supported: ThinkingEffort[];
};

export function ThinkingEffortPicker({ value, onChange, all, supported }: Props) {
  const supportedSet = new Set(supported);
  const supportsAny = supported.some((e) => e !== "off");

  // If user picks an unsupported effort (e.g. switched models), snap to the
  // closest supported level by index distance in the `all` ordering.
  const effective = supportedSet.has(value) ? value : nearestSupported(value, all, supportedSet);

  return (
    <label>
      Effort{" "}
      <select
        value={effective}
        onChange={(e) => onChange(e.target.value as ThinkingEffort)}
        disabled={!supportsAny}
        title={
          supportsAny
            ? "How hard the model should think"
            : "This model does not support extended thinking"
        }
      >
        {all.map((e) => (
          <option key={e} value={e} disabled={!supportedSet.has(e)}>
            {e}
            {supportedSet.has(e) ? "" : " (n/a)"}
          </option>
        ))}
      </select>
    </label>
  );
}

function nearestSupported(
  target: ThinkingEffort,
  all: ThinkingEffort[],
  supported: Set<ThinkingEffort>,
): ThinkingEffort {
  const targetIdx = all.indexOf(target);
  for (let d = 0; d < all.length; d++) {
    const lo = all[targetIdx - d];
    const hi = all[targetIdx + d];
    if (lo && supported.has(lo)) return lo;
    if (hi && supported.has(hi)) return hi;
  }
  return supported.values().next().value ?? "off";
}
